import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Typography,
  CircularProgress,
  Button,
  Box,
} from "@mui/material";
import {
  createPrizeCatalogueTable,
  updatePromotionsTable,
} from '../../graphql/mutations';
import { API, graphqlOperation, Storage } from "aws-amplify";
import { getConfig } from '../../utils/s3FileUtils';
import moment from "moment-timezone";
import { Formik } from "formik";
import { CopyPromotionPrizeSelection } from "./CopyPromotionPrizeSelection";
import { CopyPromotionModalProps, CopyPromoInitValues } from "./CopyPromotionModal.types";
import { getMechanicFlow, removeEmpty } from "../../utils/configUtils";
import { timezones } from "../../constants/timezones";
import { convertCopyPrizeImagesMetadata, copyPrizeImage, resetPrizeCounters } from "../../components/modals/CopyPrizeModal";
import { promoIdCharLength } from "../../pages/AssignPromotion/AssignPromotionForm";
import { updateCurrenciesWithPromotionData } from "../../pages/AssignPromotion/AssignPromotionPage";
import { PromotionDatesSetupComponent } from "../../pages/AssignedPromotions/PromotionDatesSetup";
import { MixCodesDetailsSetupComponent } from "../../pages/AssignedPromotions/MixCodesDetailsSetup";
import { convertConfigDataForListing } from "../../pages/AssignedPromotions/hooks/assignedPromotionsHooks";
import {
  alphaNumericProgramIdRegex,
  copyPromotionValidationSchema,
  hasValue,
  normalizeMixCodes,
  numericProgramIdRegex,
} from "./CopyPromotionValidationSchema";

export const steps = ["Setup Dates", "Setup Mix Codes", "Copy Prizes"];
export const mixCodesRef =
  "https://wiki.coke.com/confluence/pages/viewpage.action?pageId=6127906";
export const alphaNumaricRegex = /^[a-zA-Z0-9]+$/;
const uniqid = require("uniqid");

const CopyPromotionModal: React.FC<CopyPromotionModalProps> = ({
  open,
  onClose,
  selectedPromoId,
  campaignData,
  campaignConfigsData,
  setCampaignConfigsData,
  sourcePrizes,
  setNotificationState
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [configurationData, setConfigurationData] = useState<any>(null);
  const [initialState, setInitialState] = useState<CopyPromoInitValues>(null);

  useEffect(() => {
    const getConfigurationData = async () => {
      if (selectedPromoId) {
        setInitialState(null);
        setLoading(true);
        setApiError(null);
        setActiveStep(0);

        try {
          const configFileName = `${selectedPromoId}/conf.txt`;
          const config = await getConfig(configFileName);
          const mechanic = getMechanicFlow(config);

          // reset mixcodes related settings
          delete config.flow[mechanic]?.params?.lotIds;
          delete config.flow[mechanic]?.params?.campaignIds;

          setConfigurationData(config);

          const initalValues = {
            startDate: config.configurationParameters?.configurationStartUtc,
            endDate: config.configurationParameters?.configurationEndUtc,
            timezone: config.configurationParameters?.configurationDatesTimezone,
            tempParams: {
              burnPincodes: false,
              codeType: [],
              pincodeOriginValitidy: false,
            },
            params: config.flow[mechanic]?.params,
            secrets: {
              mixCodesParameters: [{
                programID: '',
                secret: '',
                uri: ''
              }]
            },
            checkerLambdas: config.flow[mechanic]?.checkerLambdas ? config.flow[mechanic].checkerLambdas.filter(item => item !== "pincodeOriginValidityCheckerLambda") : [],
            flowLambdas: config.flow[mechanic]?.flowLambdas ? config.flow[mechanic].flowLambdas.filter(item => item !== "burnPincodes") : [],
            selectedPromoId,
          };

          setInitialState(initalValues);
        } catch (e) {
          setApiError("Failed to load original promotion configuration.");
          setNotificationState({
            open: true,
            title: 'Failed to load original promotion configuration.',
            content: '',
            level: 'error',
          });
          onClose();
          setConfigurationData(null);
        } finally {
          setLoading(false);
        }
      }
    }
    getConfigurationData();
  }, [selectedPromoId, setNotificationState, onClose]);

  const handleNext = async (formik: any) => {
    if (activeStep === 1) {
      const errors = await formik.validateForm();
      if (errors?.secrets?.mixCodesParameters) {
        formik.setFieldTouched('secrets.mixCodesParameters', true, true);
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const invertedTimeZones = useMemo(() =>
    Object.entries(timezones)
      .reduce<Record<string, string>>((acc, [key, value]) => ({ ...acc, [value]: key }), {})
    , [])

  const prepareConfigToCopy = (values) => {
    const { startDate, endDate, timezone, params, secrets, checkerLambdas, flowLambdas } = values;

    let newConfigData = { ...configurationData };
    const mechanic = getMechanicFlow(newConfigData);

    newConfigData.configurationId = `${(uniqid() + uniqid()).substring(0, promoIdCharLength)}`;
    newConfigData.configurationParameters.additionalInformation.name = createNewConfigName();
    newConfigData.configurationParameters.configurationDatesTimezone = invertedTimeZones[timezone] || moment.tz.guess() || 'Europe/London';
    newConfigData.configurationParameters.configurationStartUtc = moment.tz(startDate, newConfigData.configurationParameters.configurationDatesTimezone).utc().valueOf();
    newConfigData.configurationParameters.configurationEndUtc = moment.tz(endDate, newConfigData.configurationParameters.configurationDatesTimezone ).utc().valueOf();
    newConfigData.flow[mechanic].params = params;
    newConfigData.flow[mechanic].secrets = secrets;
    newConfigData.flow[mechanic].checkerLambdas = checkerLambdas;
    newConfigData.flow[mechanic].flowLambdas = flowLambdas;

    // promotion image and prize-pincodes mappings will not be copied
    delete newConfigData.configurationParameters.additionalInformation?.imgUrl;
    delete newConfigData.flow[mechanic]?.params?.winningPrizesPerPinCodes;
    const sanitizedConfig = removeEmpty(newConfigData);
    const mixCodesParameters = normalizeMixCodes(sanitizedConfig.flow[mechanic]?.secrets?.mixCodesParameters || []);
    const hasMixcodes = mixCodesParameters.some((entry) =>
      hasValue(entry.programId) &&
      hasValue(entry.secret) &&
      hasValue(entry.uri) &&
      (alphaNumericProgramIdRegex.test(entry.programId) || numericProgramIdRegex.test(entry.programId))
    );

    if (sanitizedConfig.flow[mechanic]?.secrets) {
      sanitizedConfig.flow[mechanic].secrets.mixCodesParameters = mixCodesParameters.filter((entry) =>
        hasValue(entry.programId) || hasValue(entry.secret) || hasValue(entry.uri)
      );
    }

    const pincodeLambdas = ['burnPincodes'];
    if (mechanic === 'redeemPincodeForCurrencies' || mechanic === 'autoRedeemCnG') {
        pincodeLambdas.push('pincodeToCurrency')
    }
    if (hasMixcodes) {
        sanitizedConfig.proofType = 'mixCodes';
        sanitizedConfig.flow[mechanic].flowLambdas = Array.from(new Set([...pincodeLambdas, ...(sanitizedConfig.flow[mechanic].flowLambdas ?? [])]))
    } else {
        if(sanitizedConfig.proofType === 'mixCodes') {
          delete sanitizedConfig.proofType;
        }
        sanitizedConfig.flow[mechanic].flowLambdas = sanitizedConfig.flow[mechanic].flowLambdas?.filter((flowLambda) => !pincodeLambdas.includes(flowLambda));
    }
    return sanitizedConfig;
  }

  const saveConfig = async (config) => {
    await Storage.put(
      `${config.configurationId}/conf.txt`,
      JSON.stringify(removeEmpty(config)),
      {
        contentType: "text/plain",
      }
    );
  }

  const updatedCampaign = async (config) => {
    const updatedCampaignData = {
      ...campaignData,
      configurations:
        campaignData.configurations !== null && Array.isArray(campaignData.configurations)
          ? [...campaignData.configurations, config.configurationId]
          : [config.configurationId],
      last_modified: moment().toDate().getTime(),
    };

    await API.graphql({
      query: updatePromotionsTable,
      variables: { input: updatedCampaignData },
    });
  }

  const prepareCopyPrizeObject = async (prizeObj, config) => {
    const newPrizeId = uniqid();
    const prizeObjCopy = resetPrizeCounters(prizeObj, true);
    const mechanic = getMechanicFlow(config);

    for (let key in prizeObjCopy) {
      if (key === 'configuration_id') prizeObjCopy[key] = config.configurationId;

      if (key === 'prize_id') prizeObjCopy[key] = newPrizeId;

      // The default state of the prize will be INACTIVE for IW and Collect and Get prizes.
      if (key === 'active' && (mechanic === 'instantWin' || mechanic === 'redeemPincodeForCurrencies')) {
        prizeObjCopy[key] = false;
      }

      if ((key === 'start_date' || key === 'end_date')) {
        if (mechanic === 'redeemPincodeForCurrencies') {
          // For the C&G the start and end dates need to be auto-set in the past. That way we will avoid any prize being available for distribution without the business knowing it
          prizeObjCopy[key] = Date.now() - 86400000; // 1 day ago
        } else {
          // if the user changes the datetime from datepicker, it will become a datetime string
          if (typeof prizeObjCopy[key] !== 'number') {
            prizeObjCopy[key] = Date.parse(prizeObjCopy[key]);
          }

          if (config.configurationParameters.configurationStartUtc > prizeObjCopy[key]) {
            prizeObjCopy[key] = config.configurationParameters.configurationStartUtc;
          }
        }
      }

      if (key === 'img_url') {
        prizeObjCopy[key] = await copyPrizeImage(prizeObj[key], newPrizeId, config.configurationId);
      }

      if (key === 'images_metadata' && prizeObjCopy[key]) {
        prizeObjCopy[key] = convertCopyPrizeImagesMetadata(prizeObj[key], newPrizeId, config.configurationId);
      }

      if (prizeObjCopy[key] === null || prizeObjCopy[key] === "" || key === 'winning_ratio') {
        delete prizeObjCopy[key];
      }
    };

    return prizeObjCopy;
  }

  const savePrizes = async (prizes, config) => {
    return await Promise.all(
      prizes.map(async (prize) => {
        const copyObj = await prepareCopyPrizeObject(prize, config);
        await API.graphql(graphqlOperation(createPrizeCatalogueTable, { input: copyObj }));
      })
    )
  }

  const handleSave = async (values, formik) => {
    const errors = await formik.validateForm();
    if (errors?.secrets?.mixCodesParameters) {
      formik.setFieldTouched('secrets.mixCodesParameters', true, true);
      setActiveStep(1);
      return;
    }

    setLoading(true);
    const newConfigData = prepareConfigToCopy(values);
    try {
      await saveConfig(newConfigData);
      await updatedCampaign(newConfigData);
      await updateCurrenciesWithPromotionData({
        campId: newConfigData.campaignId,
        promoId: newConfigData.configurationId,
        availableCurrencies: newConfigData.tempParams?.availableCurrencies || [],
        selectedCurrencyIds: newConfigData?.configurationParameters?.currencies || [],
      });

      if (values.tempParams?.prizes && values.tempParams.prizes.length) {
        await savePrizes(values.tempParams?.prizes, newConfigData);
      }

      setNotificationState({
        open: true,
        title: 'Promotion copied successfully!',
        content: '',
        level: 'success',
      });

      setCampaignConfigsData([...campaignConfigsData, convertConfigDataForListing(newConfigData)])
      onClose();
    } catch (error: any) {
      console.error("Error copying promotion:", error);
    } finally {
      setLoading(false);
    }
  };

  const createNewConfigName = (): string => {
    const currName = configurationData?.configurationParameters?.additionalInformation?.name;
    if (!currName) return "_copy_1";
    const baseName = currName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const copyRegex = new RegExp(`^${baseName}_copy_(\\d+)$`, "i");
    let maxNumber = 0;
    campaignConfigsData.forEach(config => {
      const matching = config.promotionName?.match(copyRegex);
      if (matching && matching[1]) {
        const num = parseInt(matching[1], 10);
        if (!isNaN(num) && num > maxNumber) {
          maxNumber = num;
        }
      }
    });

    return `${currName}_copy_${maxNumber + 1}`;
  };

  const getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          <PromotionDatesSetupComponent />
        )
      case 1:
        return (
          <MixCodesDetailsSetupComponent />
        );
      case 2:
        return (
          <CopyPromotionPrizeSelection sourcePrizes={sourcePrizes} />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    initialState &&
    <Formik
      initialValues={initialState}
      validationSchema={copyPromotionValidationSchema}
      onSubmit={() => { }}
    >
      {(formik) => (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>Copy Promotion Wizard</DialogTitle>
          <DialogContent>
            <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {getStepContent(activeStep)}
            {apiError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {apiError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Box sx={{ flex: "1 1 auto" }} />
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack} disabled={loading} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => handleNext(formik)}
                disabled={loading}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleSave(formik.values, formik)}
                disabled={
                  loading
                }
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Finish & Copy"
                )}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Formik>
  );
};

export { CopyPromotionModal };
